import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';

import './App.css';
import LoginForm from './LoginForm';
import MyAppBar from './MyAppBar';

const NotificationReceived = lazy(() => import('./NotificationReceived'));
const RegBy = lazy(() => import('./RegBy'));
const SmsReceive = lazy(() => import('./SMS_Receive'));
const G9PortsVoip = lazy(() => import('./G9_Ports_Voip'));
const GoipStats = lazy(() => import('./GoipStats'));
const Advanced = lazy(() => import('./Advanced'));
const ImeiConfig = lazy(() => import('./ImeiConfig'));
const Bridge = lazy(() => import('./Bridge'));
const FinancialTrackerPage = lazy(() => import('./tracker/pages/FinancialTrackerPage'));
const GatezOffline = lazy(() => import('./Gatez_offline'));
const Loader = lazy(() => import('./Loader'));
const GoipManagementOffline = lazy(() => import('./GoipManagement_offline'));
const Scheduler = lazy(() => import('./Scheduler'));
const Simregbot = lazy(() => import('./Simregbot'));
const AutoLoad = lazy(() => import('./Auto_Load'));
const SmsLogs = lazy(() => import('./SMS_Logs'));
const CallLogs = lazy(() => import('./Call_Logs'));
const Snapshot = lazy(() => import('./Snapshot'));
const PrinterDashboard = lazy(() => import('./PrinterDashboard'));
const Earntracker = lazy(() => import('./Earntracker'));
const TrackerDashboard = lazy(() => import('./tracker/components/TrackerDashboard'));
const TransactionHistoryPage = lazy(() => import('./tracker/pages/TransactionHistoryPage'));
const G9Auto = lazy(() => import('./G9_Auto'));
const G9PortsBingo = lazy(() => import('./G9PortsBingo'));
const G9PortsBingoIndividual = lazy(() => import('./G9PortsBingoIndividual'));
const OtpServicesManager = lazy(() => import('./OtpServicesManager'));
const SmsVoipManager = lazy(() => import('./SmsVoipManager'));
const VoipReportToolPage = lazy(() => import('./VoipReportToolPage'));
const CatchedNumbers = lazy(() => import('./CatchedNumbers'));
const MongoAdmin = lazy(() => import('./MongoAdmin'));
const SimRegistrationControl = lazy(() => import('./SimRegistrationControl'));
const SolarDashboard = lazy(() => import('./SolarDashboard'));

const LOGIN_EXPIRATION_MS = 24 * 60 * 60 * 1000;

const appRoutes = [
  { path: '/', element: <GatezOffline /> },

  { path: '/ImeiConfig', element: <ImeiConfig /> },
  { path: '/G9_Ports_Voip', element: <G9PortsVoip /> },
  { path: '/Advanced', element: <Advanced /> },
  { path: '/RegBy', element: <RegBy /> },
  { path: '/G9_Auto', element: <G9Auto /> },
  { path: '/Earntracker', element: <Earntracker /> },
  { path: '/Loader', element: <Loader /> },
  { path: '/GoipManagement_offline', element: <GoipManagementOffline /> },
  { path: '/Scheduler', element: <Scheduler /> },
  { path: '/Auto_Load', element: <AutoLoad /> },
  { path: '/Bridge', element: <Bridge /> },
  { path: '/NotificationReceived', element: <NotificationReceived /> },
  { path: '/Simregbot', element: <Simregbot /> },
  { path: '/SMS_Logs', element: <SmsLogs /> },
  { path: '/Call_Logs', element: <CallLogs /> },
  { path: '/Snapshot', element: <Snapshot /> },
  { path: '/Printer', element: <PrinterDashboard /> },
  { path: '/GoipStats', element: <GoipStats /> },
  { path: '/G9PortsBingo', element: <G9PortsBingo /> },
  { path: '/G9PortsBingoIndividual', element: <G9PortsBingoIndividual /> },
  { path: '/OtpServices', element: <OtpServicesManager /> },
  { path: '/SmsVoipManager', element: <SmsVoipManager /> },
  { path: '/VoipReportTool', element: <VoipReportToolPage /> },
  { path: '/CatchedNumbers', element: <CatchedNumbers /> },
  { path: '/MongoAdmin', element: <MongoAdmin /> },
  { path: '/SMS_Receive', element: <SmsReceive /> },
  { path: '/SimRegistrationControl', element: <SimRegistrationControl /> },
  { path: '/SolarDashboard', element: <SolarDashboard /> },
];

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPhoneUser, setIsPhoneUser] = useState(false);

  useEffect(() => {
    const savedLoginState = localStorage.getItem('isLoggedIn');
    const loginTimestamp = localStorage.getItem('loginTimestamp');

    if (savedLoginState !== null && loginTimestamp !== null) {
      const now = Date.now();
      const timeElapsed = now - parseInt(loginTimestamp, 10);

      if (timeElapsed < LOGIN_EXPIRATION_MS) {
        setIsLoggedIn(JSON.parse(savedLoginState));
        setIsPhoneUser(localStorage.getItem('isPhoneUser') === 'true');
      } else {
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('loginTimestamp');
        localStorage.removeItem('isPhoneUser');
      }
    }

    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    localStorage.setItem('isLoggedIn', JSON.stringify(isLoggedIn));

    if (isLoggedIn) {
      localStorage.setItem('loginTimestamp', Date.now().toString());
    } else {
      localStorage.removeItem('loginTimestamp');
      localStorage.removeItem('isPhoneUser');
    }
  }, [isLoggedIn, isLoaded]);

  const handleLogin = async (username, password, otp) => {
    try {
      const response = await fetch('https://frek.cc/proxy/38.54.86.24:19564/validate-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, otp }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsLoggedIn(true);
      } else {
        throw new Error(data.error || 'Invalid OTP or login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error; // Pass the error to the LoginForm component for handling
    }
  };

  const handleSetupTOTP = async (username, password) => {
    try {
      const response = await fetch('https://frek.cc/proxy/38.54.86.24:19564/generate-otp-secret', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();
      if (response.ok) {
        return data;
      } else {
        throw new Error(data.error || 'Failed to set up OTP');
      }
    } catch (error) {
      console.error('Setup TOTP error:', error);
      throw error; // Pass the error to the LoginForm component for handling
    }
  };

  if (!isLoaded) {
    return null; // Display nothing or a loader until the component is loaded
  }

  if (!isLoggedIn) {
    return (
      <LoginForm onLogin={handleLogin} onSetupTOTP={handleSetupTOTP} />
    );
  }

  return (
    <div className="App">
      <MyAppBar />
      <Suspense fallback={<div className="App-loading">Loading...</div>}>
        <Routes>
          {isPhoneUser ? (
            <Route path="*" element={<Navigate to="/" replace />} />
          ) : (
            <>
              {appRoutes.map(({ path, element }) => (
                <Route key={path} path={path} element={element} />
              ))}
              <Route path="/Fintracker" element={<FinancialTrackerPage />}>
                <Route index element={<TrackerDashboard />} />
                <Route path="history" element={<TransactionHistoryPage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
